import { useParallaxScene } from '../../application/motion/useParallaxScene';

/**
 * The ambient background the browse screens stand in (SPEC-023).
 *
 * habb.me's `AmbientBands` is the reference — a faint, always-moving layer that
 * makes the app feel like one place rather than a stack of documents — and this
 * is that experience rebuilt out of what this product already has, not a port.
 * Three things came across; three did not, and the three that did not are the
 * interesting half.
 *
 * WHAT CAME ACROSS. One layer, shared, so two screens cannot drift into
 * backgrounds that nearly match. Motion slow enough to sit behind live copy.
 * A drift that never reverses and never seams.
 *
 * WHAT DID NOT, AND WHY:
 *
 *   1. THE PERSPECTIVE. habb.me tilts its layer inside `perspective: 1100px`,
 *      which is the best part of that effect — a flat plane can only slide, and
 *      a slide reads as the page moving. It cannot be had here. `landingScene.css`
 *      records what this repo already paid to learn: `perspective` on a
 *      `position: fixed` element makes every layer inside it silently fail to
 *      PAINT on real iOS Safari, while still reporting complete, visible and
 *      correctly sized. This layer is fixed and this is a phone-first product, so
 *      depth comes from layer order and differential translation instead — the
 *      same trade the landing scene makes two files away.
 *
 *   2. THE DIAL. habb.me solves "there is always another band coming" by placing
 *      arcs around a circle and turning it, which costs one transform and never
 *      runs out. A circle is the right answer for a screen-sized fold and the
 *      wrong one for a catalogue you scroll: what a visitor here travels along is
 *      a COLUMN, so the field is a column too — the pieces are laid out down a
 *      strip twice the frame's height, repeated once, and the strip rises. When
 *      the strip has moved exactly half its height the second run is where the
 *      first was, so the loop closes with no seam, for the same one transform.
 *
 *   3. THE HOOK. `useAmbientParallax` would be a second way to do a thing this
 *      repo already does. `useParallaxScene` is the established one: same rAF
 *      discipline, same `pointer: fine` and `prefers-reduced-motion` gates, and
 *      it damps toward the pointer rather than easing after it — plus a third
 *      channel, `--parallax-scroll`, which is the one that matters on a phone.
 *      No touch device has a hover position, so on the target device the pointer
 *      half of habb.me's effect is simply absent; here, scrolling the catalogue
 *      moves the ground under it.
 *
 * WHY IT IS A COMPONENT. Everything below is the same drawing at every address —
 * the same seven pieces, the same depths, the same clock — and a screen chooses
 * nothing. Two screens each carrying their own nearly-identical field is two
 * backgrounds to keep in step. (habb.me needed a `variant` prop because its two
 * hosts anchored the layer differently; both of this one's hosts scroll, so
 * there is one anchor and no prop.)
 *
 * WHY IT IS NOT IN THE SHELL. It is only right on the two TABBED screens. The
 * landing is a fold with a garden scene of its own, and a plant detail page is
 * immersive — artwork to every edge, with nothing behind it to see. A flag on
 * `chromeForRoute` threaded down to one mount would put the decision two files
 * away from both screens that make it, for one line each.
 */

/**
 * The pieces, in the order they are stacked down the strip.
 *
 * ORDER, NOT COORDINATES, and that is the whole of the no-overlap guarantee
 * (SPEC-023 D-7). These used to carry a `top` as a percentage of the strip while
 * their SIZE was a percentage of the frame's WIDTH — two different axes — so a
 * piece's height in strip-percent depended on the viewport's aspect ratio, and a
 * spacing that cleared on a tall phone collided on a short wide window. No table
 * of numbers fixes that; the axes have to be the same one.
 *
 * So the strip is a flex column and the pieces are laid out by FLOW. Each one is
 * as tall as its own artwork, each is followed by the same gap, and the next one
 * starts below it — at every viewport shape, for free, with nothing to keep in
 * step. `overlap` stops being a value to tune and becomes a thing that cannot
 * happen.
 *
 * `width` is a percentage of the frame, and most of these are MOTIF-SIZED — the
 * two sprays are nearly as wide as the shell. At the first sizes, 16–42%, every
 * piece sat wholly inside the frame with room around it, which reads as a sticker
 * placed on the page. A background motif is a drawing the frame is a window onto:
 * it runs off the edges, you never see its outline, and the eye completes it.
 *
 * `depth` is the parallax multiplier, 0 (pinned) to 1 (nearest), correlated with
 * SIZE on purpose — a big piece that moves like a far one reads as a poster hung
 * in front of the screen. The line-art stem is the deliberate exception: it is
 * the palest thing in the set, so it can sit near without shouting.
 *
 * `side` picks which edge it hangs off. Nothing is centred: the middle of the
 * frame is the reading column, and the mask fades hardest through it. Sides
 * alternate down the list so the eye is not walked down one margin.
 */
const PIECES = Object.freeze([
    Object.freeze({ name: 'leaf-spray', side: 'right', width: 96, depth: 0.85 }),
    Object.freeze({ name: 'agave-blades', side: 'left', width: 88, depth: 0.7 }),
    Object.freeze({ name: 'stem-buds', side: 'right', width: 52, depth: 0.9 }),
    Object.freeze({ name: 'dots-arc', side: 'left', width: 40, depth: 0.35 }),
    Object.freeze({ name: 'willow-spray', side: 'right', width: 94, depth: 0.8 }),
    Object.freeze({ name: 'flower-star', side: 'left', width: 46, depth: 0.5 }),
    Object.freeze({ name: 'fern-tuft', side: 'right', width: 66, depth: 0.6 }),
]);

/**
 * The strip is the piece list TWICE, in the same order.
 *
 * The rise translates the strip up by half its own height, so when it has
 * travelled that far the second run is standing exactly where the first began —
 * the last frame of the animation is the first frame, and the loop has no seam.
 * That only holds if the two runs are identical and evenly spaced, which is why
 * the gap is a `margin-bottom` on every piece rather than a flex `gap`: a gap
 * applies BETWEEN items, so a fourteen-item strip would have thirteen of them and
 * half the height would land half a gap away from where the second run starts.
 */
const RUNS = Object.freeze([0, 1]);

export default function AmbientGarden() {
    // The same hook the landing scene drives its garden with. It attaches no
    // pointer listener without a fine pointer and does nothing at all under
    // `prefers-reduced-motion: reduce`, so both gates are honoured here for free.
    const sceneRef = useParallaxScene();

    return (
        <div className="ambient-garden" ref={sceneRef} aria-hidden="true">
            <div className="ambient-garden__field">
                {RUNS.map((offset) => PIECES.map((piece) => (
                    <div
                        key={`${piece.name}-${offset}`}
                        className={`ambient-garden__piece ambient-garden__piece--${piece.side}`}
                        style={{
                            '--depth': piece.depth,
                            '--piece-width': `${piece.width}%`,
                        }}
                    >
                        <img
                            className={`ambient-garden__art ambient-garden__art--${piece.name}`}
                            src={`/assets/ambient/${piece.name}.webp`}
                            alt=""
                            /* Decoration that is never the reason a screen is
                               waited for: the catalogue's own cards are what a
                               visitor came for, and these must not compete with
                               them for the first connections. */
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                )))}
            </div>
        </div>
    );
}

export { PIECES as AMBIENT_PIECES, RUNS as AMBIENT_RUNS };
